/* /app/frontend/src/components/dealroom/MoSCompletedSummary.jsx
 *
 * iter-398B — Replaces the "Issue Memorandum of Sale" NBA card with
 * a completed summary once the MoS has been persisted.  Reads from
 * the canonical `/memorandum-of-sale/status` endpoint — no local
 * "just did it" flag.  If the endpoint reports uncompleted, this
 * component renders null and the caller falls back to the NBA CTA.
 */
import React, { useState } from "react";
import { CheckCircle2, Target, ChevronDown, ChevronUp } from "lucide-react";
import { formatKeyDate } from "./KeyDatesSummary";


function _fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}


// Same canonical date + formatter as Key Dates (sent_at; never the recording timestamp).
const issuedText = (status) =>
  status.sent_at ? `Issued ${formatKeyDate(String(status.sent_at).slice(0, 10))}` : "Sent · date not recorded";

const OUTCOME_LABEL = {
  sent_from_chainflow:       "Sent via ChainFlow",
  already_sent_externally:   "Already Sent",
};


export default function MoSCompletedSummary({ status }) {
  const [open, setOpen] = useState(false);
  if (!status || !status.completed) return null;
  const label = OUTCOME_LABEL[status.outcome] || "Completed";
  const recipients = Array.isArray(status.recipients) ? status.recipients : [];
  return (
    <div
      className="rounded-2xl bg-emerald-50/60 p-4 sm:p-5"
      style={{ border: "1px solid rgba(16,185,129,0.35)" }}
      data-testid="mos-completed-summary"
    >
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-white ring-1 ring-emerald-200 text-[10.5px] font-bold uppercase tracking-[0.14em] text-emerald-700">
          <Target size={10} strokeWidth={2.4} />
          Memorandum of Sale
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[13.5px] font-bold text-slate-900 inline-flex items-center gap-1.5" data-testid="mos-completed-title">
            <CheckCircle2 size={13} className="text-emerald-600" strokeWidth={2.4} />
            Completed · <span className="text-emerald-700">{label}</span>
          </p>
          <p className="mt-0.5 text-[11.5px] text-slate-500" data-testid="mos-completed-when">
            {issuedText(status)}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-emerald-700 hover:underline px-2 py-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
          aria-expanded={open}
          data-testid="mos-completed-toggle-details"
        >
          {open ? "Hide details" : "View details"}
          {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        </button>
      </div>
      {open && (
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="mos-completed-details">
          <div className="rounded-lg bg-white px-3 py-2 ring-1 ring-emerald-100">
            <dt className="text-[10px] uppercase tracking-[0.10em] font-bold text-slate-500">Path</dt>
            <dd className="mt-0.5 text-[12.5px] font-medium text-slate-800">{label}</dd>
          </div>
          <div className="rounded-lg bg-white px-3 py-2 ring-1 ring-emerald-100">
            <dt className="text-[10px] uppercase tracking-[0.10em] font-bold text-slate-500">Recorded in ChainFlow</dt>
            <dd className="mt-0.5 text-[12.5px] font-medium text-slate-800 tabular-nums" data-testid="mos-completed-recorded-at">{_fmtWhen(status.completed_at)}</dd>
          </div>
          <div className="rounded-lg bg-white px-3 py-2 ring-1 ring-emerald-100 sm:col-span-1">
            <dt className="text-[10px] uppercase tracking-[0.10em] font-bold text-slate-500">Recipients</dt>
            <dd className="mt-0.5 text-[12.5px] font-medium text-slate-800 truncate" title={recipients.join(", ")}>
              {recipients.length ? recipients.join(", ") : (status.outcome === "already_sent_externally" ? "Handled outside ChainFlow" : "—")}
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
}
